import { useState } from 'react';
import { formatTeamData, submitEstimate } from '../../../api';
import { monthsToDays } from '../../../utils/calculatorUtils';
import {
  TEAM_COMPOSITION,
  mapChallengeToApiValue,
  mapCompanyLocationToApiValue,
  mapServiceToApiValue,
} from '../calculatorConstants';
import { calculateCosts } from '../costCalculation';
import type { CalculatorFormState, SubmissionStatus } from '../types';

const DEFAULT_SPRINT_DURATION = 2;

interface SubmissionOptions {
  onSuccess?: () => void;
}

export const useCalculatorSubmission = ({ onSuccess }: SubmissionOptions = {}) => {
  const [status, setStatus] = useState<SubmissionStatus>({
    loading: false,
    error: null,
    success: false,
  });

  const submit = async (formData: CalculatorFormState) => {
    if (!formData.email || !formData.acceptTerms) {
      setStatus({
        loading: false,
        error: 'Proszę podać adres email i zaakceptować warunki',
        success: false,
      });
      return;
    }

    setStatus({ loading: true, error: null, success: false });

    try {
      const costs = calculateCosts(formData);
      const projectName = formData.challenges[0] || 'Nowy projekt';

      const apiWorkers = formatTeamData(
        formData.teamComposition,
        TEAM_COMPOSITION as unknown as Record<string, { title: string; hourlyRate: number }>,
      );

      const tranches = Array(costs.trancheCount)
        .fill(null)
        .map((_, index) => ({
          number: index + 1,
          price: costs.costPerTranche,
        }));

      const submissionData = {
        email: formData.email,
        estimate: {
          dnd_workshops_costs: Number(costs.designCost),
          travel_costs: 0,
          tranches,
          total_costs: Number(costs.total),
          workers: apiWorkers,
          delivery_estimate_days: monthsToDays(formData.projectDuration),
          milestones: Number(costs.milestones),
          currency: costs.currency,
          sprint_duration: DEFAULT_SPRINT_DURATION,
          project_name: projectName,
          questionnaire: {
            challenge: formData.challenges[0]
              ? mapChallengeToApiValue(formData.challenges[0])
              : 'poc',
            services: formData.services.map(s => mapServiceToApiValue(s)),
            company_location: mapCompanyLocationToApiValue(formData.workshopType),
            details: formData.technologiesText || null,
          },
        },
      };

      try {
        const result = await submitEstimate(submissionData);
        setStatus({ loading: false, error: null, success: true, responseData: result });
        onSuccess?.();
      } catch (apiError) {
        console.error('Error submitting form:', apiError);

        if (apiError instanceof TypeError && apiError.message.includes('Failed to fetch')) {
          console.log('CORS error but treating submission as successful');
          setStatus({
            loading: false,
            error: null,
            success: true,
            responseData: { success: true },
          });
          onSuccess?.();
          return;
        }

        setStatus({
          loading: false,
          error:
            apiError instanceof Error
              ? apiError.message
              : 'Wystąpił błąd podczas wysyłania formularza',
          success: false,
        });
      }
    } catch (error) {
      console.error('Error preparing form data:', error);
      setStatus({
        loading: false,
        error:
          error instanceof Error
            ? error.message
            : 'Wystąpił błąd podczas przygotowywania formularza',
        success: false,
      });
    }
  };

  return { status, submit };
};
